import React, { useState, useEffect } from 'react';
import { saveAs } from 'file-saver';

/**
 * Integration component for the Decentralized Sharing platform
 * This component acts as a bridge between the main app and the decentralized sharing platform
 */
function DecentralizedSharing() {
  const [documentsToShare, setDocumentsToShare] = useState([]);
  const [selectedDocument, setSelectedDocument] = useState(null);
  
  // Load available safety documentation from localStorage on component mount
  useEffect(() => {
    const loadSavedDocumentation = () => {
      try {
        // Log localStorage keys for debugging
        const localStorageKeys = [];
        for (let i = 0; i < localStorage.length; i++) {
          localStorageKeys.push(localStorage.key(i));
        }
        console.log('Available localStorage keys:', localStorageKeys);
        
        // Check for saved safety documentation in localStorage
        const savedIncidentsJSON = localStorage.getItem('safetyDocumentation');
        console.log('Found safetyDocumentation in localStorage:', !!savedIncidentsJSON);
        
        if (savedIncidentsJSON) {
          const savedData = JSON.parse(savedIncidentsJSON);
          console.log('Parsed safety documentation data:', savedData);
          
          // Format documents for the dropdown
          const formattedDocs = [{
            id: 'safety_documentation',
            title: 'Safety Documentation - Incidents',
            data: savedData,
            type: 'safety_plan',
            created: new Date().toISOString(),
            description: 'Documented incidents with details and evidence'
          }];
          
          setDocumentsToShare(formattedDocs);
          console.log('Added safety documentation to documents to share');
        }
        
        // Check if there are any encrypted documents stored
        const encryptedDocsStr = localStorage.getItem('encryptedDocuments');
        console.log('Found encryptedDocuments in localStorage:', !!encryptedDocsStr);
        
        if (encryptedDocsStr) {
          const encryptedDocs = JSON.parse(encryptedDocsStr);
          console.log('Found encrypted docs:', Object.keys(encryptedDocs).length);
          
          const encryptedDocsList = Object.keys(encryptedDocs).map(key => ({
            id: key,
            title: `Encrypted Document - ${key}`,
            type: 'encrypted',
            created: encryptedDocs[key].timestamp || new Date().toISOString(),
            data: encryptedDocs[key].data,
            description: 'Encrypted safety documentation'
          }));
          
          setDocumentsToShare(prev => [...prev, ...encryptedDocsList]);
          console.log('Added encrypted documents to documents to share');
        }
        
        // If no documents were found, log a message
        if (!savedIncidentsJSON && !encryptedDocsStr) {
          console.log('No documents found in localStorage to share');
        }
      } catch (error) {
        console.error('Error loading saved documentation:', error);
      }
    };
    
    loadSavedDocumentation();
  }, []);
  
  // Define sharing app URL using environment variable or appropriate URLs
  // Priority is given to the specific URLs even in development
  const SHARING_APP_URL = process.env.REACT_APP_SHARING_URL || 
    (window.location.hostname.includes('stopvio.com') ? 'https://share.stopvio.com' : 
     window.location.hostname.includes('no2harm.com') ? 'https://share.no2harm.com' : 
     // Default to stopvio share URL for development environments
     'https://share.stopvio.com');
  
  // Launch the decentralized sharing app in a new tab
  const launchSharingApp = () => {
    window.open(SHARING_APP_URL, '_blank');
  };
  
  // Launch the sharing app and prepare to share the selected document
  const shareSelectedDocument = () => {
    if (!selectedDocument) return;
    
    try {
      // Format the document data for sharing
      const documentForSharing = {
        ...selectedDocument,
        shared_timestamp: new Date().toISOString(),
        shared_from: window.location.hostname,
        format_version: "2.0"
      };
      
      // Store the document data in sessionStorage for the sharing app to pick up
      sessionStorage.setItem('document_to_share', JSON.stringify(documentForSharing));
      
      // Record that this document was shared
      const sharedDocuments = JSON.parse(localStorage.getItem('sharedDocuments') || '{}');
      sharedDocuments[selectedDocument.id] = {
        title: selectedDocument.title,
        timestamp: new Date().toISOString(),
        shared: true
      };
      localStorage.setItem('sharedDocuments', JSON.stringify(sharedDocuments));
      
      // Prepare document metadata for message creation
      const messageMetadata = {
        document_type: selectedDocument.type || "safety_documentation",
        document_id: selectedDocument.id,
        document_title: selectedDocument.title,
        incident_count: selectedDocument.data?.incidents?.current?.length || 0,
        artifact_count: countArtifacts(selectedDocument.data),
        sharing_app_url: SHARING_APP_URL,
        shared_timestamp: new Date().toISOString()
      };
      
      // Store metadata for the messaging app to access
      sessionStorage.setItem('document_message_metadata', JSON.stringify(messageMetadata));
      
      // Launch the sharing app with share action and metadata
      window.open(
        `${SHARING_APP_URL}?action=share&source=safety_app&document_id=${selectedDocument.id}&has_metadata=true`, 
        '_blank'
      );
      
      // Show confirmation message
      setTimeout(() => {
        alert(`Document "${selectedDocument.title}" has been prepared for sharing. The decentralized sharing app has been opened in a new tab.`);
      }, 500);
    } catch (error) {
      console.error('Error preparing document for sharing:', error);
      alert(`There was an error preparing your document for sharing: ${error.message}`);
    }
  };
  
  // Helper function to count the total number of IPFS artifacts in a document
  const countArtifacts = (data) => {
    if (!data || !data.incidents || !data.incidents.current) return 0;
    
    let count = 0;
    
    // Count artifacts in current incidents
    if (Array.isArray(data.incidents.current)) {
      data.incidents.current.forEach(incident => {
        if (incident.ipfsArtifacts && Array.isArray(incident.ipfsArtifacts)) {
          count += incident.ipfsArtifacts.length;
        }
      });
    }
    
    // Count artifacts in historical incidents if they exist
    if (data.incidents.history && Array.isArray(data.incidents.history)) {
      data.incidents.history.forEach(historySet => {
        if (Array.isArray(historySet)) {
          historySet.forEach(incident => {
            if (incident.ipfsArtifacts && Array.isArray(incident.ipfsArtifacts)) {
              count += incident.ipfsArtifacts.length;
            }
          });
        }
      });
    }
    
    return count;
  };
  
  // Create a JSON file with sharing instructions
  const createSharingInstructions = () => {
    const instructions = {
      type: "decentralized_sharing_instructions",
      version: "1.1",
      app_url: SHARING_APP_URL,
      introduction: {
        title: "StopVio Decentralized Sharing Network",
        description: "A secure, censorship-resistant platform for sharing permanent, verifiable information",
        purpose: "This platform enables the creation of immutable records and secure information sharing without relying on central authorities."
      },
      identity_management: {
        description: "Your cryptographic identity is your secure presence on the network",
        steps: [
          "1. Navigate to 'Your Cryptographic Identity'",
          "2. Create a new identity or import an existing one",
          "3. Choose whether to use your real name or remain pseudonymous",
          "4. Export your identity and keep it secure for future access",
          "5. Never share your secret key with anyone - it's your private signature"
        ],
        security_notes: [
          "Your identity consists of a public key (shareable) and secret key (private)",
          "The secret key allows you to sign messages, proving they came from you",
          "Export and securely store your identity to access it from different devices",
          "Consider using a password manager to store your exported identity file"
        ]
      },
      how_to_use: [
        "1. Open the Decentralized Sharing app using the URL above",
        "2. Create an identity or import your existing one",
        "3. Go to the 'Create Message' section",
        "4. Enter the content you want to share",
        "5. Select an appropriate category for your message",
        "6. Review your content carefully before publishing",
        "7. Confirm publication when prompted",
        "8. Share the resulting CID with others who need access"
      ],
      message_categories: {
        "legal_resource": "Information about legal rights, resources, or services",
        "safety_planning": "Strategies, tools, or resources for safety planning",
        "support_group": "Information about support networks or community resources",
        "legal_testimony": "Accounts or information that may have legal relevance",
        "public_notice": "General announcements or informational content",
        "general_information": "Other helpful information or resources",
        "research_finding": "Research, data, or findings related to violence prevention"
      },
      message_board: {
        description: "The message board allows you to manage and share collections of messages",
        features: [
          "Import individual messages using their CIDs",
          "Import entire message boards to see collections of resources",
          "Filter messages by category",
          "View verification status of messages",
          "Share your board CID with others to provide access to your collection"
        ]
      },
      when_to_use: [
        "Share information that needs to be permanently accessible",
        "Create records that cannot be altered or removed",
        "Establish verification of when information was published",
        "Connect with others in a decentralized manner",
        "Share legal documentation that might be needed in the future",
        "Create or access censorship-resistant resources",
        "Build community knowledge bases with verified authorship"
      ],
      when_not_to_use: [
        "When information might need to be removed later",
        "For sharing personal identifiable information",
        "For time-sensitive or emergency communications",
        "When the content includes information you don't have the right to distribute",
        "For information that could compromise someone's safety if it became public"
      ],
      warnings: [
        "All published information is permanent and cannot be removed by anyone",
        "You are legally responsible for any content you publish",
        "Do not share private information without careful consideration",
        "Always prioritize safety when sharing sensitive information",
        "Consider consulting with a legal advocate before sharing legally sensitive content",
        "Once published, information could potentially become public even if shared selectively"
      ],
      technical_details: {
        "technology": "IPFS (InterPlanetary File System)",
        "cryptography": "Ed25519 signatures for message verification",
        "storage": "Content is stored on a distributed network rather than central servers",
        "addressing": "Content is identified by its cryptographic hash (CID)",
        "verification": "Messages show as 'Verified' when signature is valid and content is unaltered"
      }
    };
    
    const blob = new Blob([JSON.stringify(instructions, null, 2)], { type: 'application/json' });
    saveAs(blob, 'decentralized_sharing_instructions.json');
  };

  return (
    <div className="card">
      <div className="card-header">
        <h2>Decentralized Sharing</h2>
      </div>
      <div className="card-body">
        <div className="warning-box">
          <h3>About Decentralized Sharing</h3>
          <p>
            The Decentralized Sharing platform allows you to publish information to the IPFS 
            network, where it becomes permanent and immutable. This is useful for:
          </p>
          <ul>
            <li>Creating permanent records of incidents</li>
            <li>Sharing resources with others without relying on central servers</li>
            <li>Establishing a timeline that cannot be altered</li>
            <li>Building community knowledge bases</li>
          </ul>
          <p>
            <strong>Important:</strong> Information published to IPFS cannot be removed after 
            publication. Use this feature with caution and only share information you have the 
            legal right to distribute.
          </p>
        </div>
        
        <div style={{ marginTop: '2rem', backgroundColor: '#f8f9fa', padding: '1.5rem', borderRadius: '0.5rem', border: '1px solid #dee2e6' }}>
          <h3>How to Use Decentralized Sharing</h3>
          
          <ol style={{ marginBottom: '1.5rem' }}>
            <li style={{ marginBottom: '0.75rem' }}>
              <strong>Create your identity</strong> - When you first open the Decentralized Sharing app, 
              you'll need to create a cryptographic identity. This can be anonymous or include a display name.
            </li>
            <li style={{ marginBottom: '0.75rem' }}>
              <strong>Export your identity for safekeeping</strong> - Your identity is the key to verifying your messages. 
              Export and store it securely to access it from other devices.
            </li>
            <li style={{ marginBottom: '0.75rem' }}>
              <strong>Create messages</strong> - Post information by creating messages in relevant categories. 
              Once published, messages become permanent and verifiable.
            </li>
            <li style={{ marginBottom: '0.75rem' }}>
              <strong>Share your content</strong> - After publishing, you'll receive a Content Identifier (CID) 
              that others can use to access your message or message board.
            </li>
          </ol>
          
          <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
            <button 
              onClick={launchSharingApp}
              className="btn btn-primary"
              style={{ fontSize: '1.1rem', padding: '0.5rem 1.25rem' }}
              title={`Opens ${SHARING_APP_URL} in a new tab`}
            >
              Open Decentralized Sharing App {SHARING_APP_URL.includes('stopvio') ? '(StopVio)' : '(No2Harm)'}
            </button>
            
            <button 
              onClick={createSharingInstructions}
              className="btn btn-secondary"
            >
              Download Detailed Instructions
            </button>
          </div>
          
          <div className="alert alert-info mt-2">
            <small><strong>Note:</strong> The "Open Decentralized Sharing App" button will take you to {SHARING_APP_URL} in a new tab. This is the integrated decentralized sharing platform where you can publish and access permanent content.</small>
          </div>
          
          <div className="alert alert-info" style={{ marginTop: '1rem', display: 'flex', alignItems: 'center' }}>
            <div style={{ marginRight: '1rem', fontSize: '1.5rem' }}>ℹ️</div>
            <div>
              <strong>New to decentralized sharing?</strong> We've created comprehensive guides to help you 
              understand and effectively use this powerful tool. See the USER-GUIDE.md and 
              DECENTRALIZED-SHARING-GUIDE.md files in the project's root directory.
            </div>
          </div>
        </div>
        
        <div style={{ marginTop: '2rem' }}>
          <h3>Export and Share Documentation</h3>
          <p>
            You can export your documentation from this app and share it using the 
            Decentralized Sharing platform. This creates an immutable record that can be accessed 
            through a unique identifier.
          </p>
          
          {documentsToShare.length > 0 ? (
            <>
              <div className="form-group">
                <label className="form-label">Select documentation to share:</label>
                <select 
                  className="form-control"
                  value={selectedDocument ? selectedDocument.id : ''}
                  onChange={(e) => {
                    const selected = documentsToShare.find(doc => doc.id === e.target.value);
                    setSelectedDocument(selected || null);
                  }}
                >
                  <option value="">Select a document</option>
                  {documentsToShare.map(doc => (
                    <option key={doc.id} value={doc.id}>
                      {doc.title}
                    </option>
                  ))}
                </select>
              </div>
              
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                <button 
                  onClick={shareSelectedDocument}
                  className="btn btn-primary"
                  disabled={!selectedDocument}
                >
                  Share Selected Document
                </button>
                
                <div className="alert alert-info" style={{ marginBottom: '0' }}>
                  <strong>What happens next:</strong> When you click "Share Selected Document":
                  <ol style={{ marginTop: '0.5rem', marginBottom: '0' }}>
                    <li>You'll be taken to the Decentralized Sharing app in a new tab</li>
                    <li>Your documentation will be pre-loaded as a draft message</li>
                    <li>Create a cryptographic identity (if you don't have one yet)</li>
                    <li>Review and publish your documentation to create a permanent record</li>
                  </ol>
                </div>
              </div>
              
              <div className="alert alert-warning" style={{ marginTop: '1rem' }}>
                <strong>Remember:</strong> Once shared to the decentralized network, documents become permanent 
                and cannot be removed. Only share information you are comfortable becoming public and permanent.
              </div>
            </>
          ) : (
            <div className="alert alert-info">
              <h4>No Documents Available</h4>
              <p>
                You need to create documentation in the Safety Plan section first. Follow these steps:
              </p>
              <ol>
                <li>Go to the "Safety Plan" tab</li>
                <li>Create one or more incidents with details and evidence</li>
                <li>Click "Export Documentation" to save your documentation</li>
                <li>Return to this section to share your documentation</li>
              </ol>
              <button 
                onClick={() => window.history.back()}
                className="btn btn-primary"
              >
                Go Back to Safety Plan
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default DecentralizedSharing;